import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Loader2, Trash2, Link2, Copy, Plus, StickyNote, Users, DollarSign, ChevronDown, ChevronUp,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem('eventism_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const fmt = (c) => ((c || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
const ts = (iso) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });

const STATUS_BADGE = {
  lead: 'bg-sky-500/15 text-sky-500 border-sky-500/30',
  pending_payment: 'bg-amber-500/15 text-amber-500 border-amber-500/30',
  active: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30',
  paused: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
  churned: 'bg-red-500/15 text-red-400 border-red-500/30',
};
const STATUSES = ['lead', 'pending_payment', 'active', 'paused', 'churned'];
const TYPES = { event_creator: 'Event creator', restaurant: 'Restaurant', venue: 'Venue', other: 'Other' };

function ContactsPanel({ client, onChanged }) {
  const [form, setForm] = useState({ name: '', role: '', email: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!form.name.trim()) { toast.error('Contact name required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/managed/admin/clients/${client.client_id}/contacts`, form, { headers: authHeaders() });
      setForm({ name: '', role: '', email: '', phone: '' });
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add'); }
    finally { setBusy(false); }
  };
  const remove = async (cid) => {
    await axios.delete(`${API}/managed/admin/clients/${client.client_id}/contacts/${cid}`, { headers: authHeaders() }).catch(() => toast.error('Delete failed'));
    onChanged();
  };
  return (
    <div className="space-y-2" data-testid="managed-contacts-panel">
      <p className="text-sm font-heading font-semibold flex items-center gap-2"><Users className="h-4 w-4 text-indigo-400" />Contacts</p>
      {(client.contacts || []).map((c) => (
        <div key={c.contact_id} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
          <div className="min-w-0 flex-1">
            <p className="font-medium truncate">{c.name} {c.role && <span className="text-xs text-muted-foreground">· {c.role}</span>}</p>
            <p className="text-xs text-muted-foreground truncate">{[c.email, c.phone].filter(Boolean).join(' · ') || 'no contact info'}</p>
          </div>
          <button onClick={() => remove(c.contact_id)} className="text-red-400 hover:text-red-300" data-testid={`contact-delete-${c.contact_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <div className="grid grid-cols-2 gap-2">
        <Input className="h-8 text-sm" placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name-input" />
        <Input className="h-8 text-sm" placeholder="Role / position" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="contact-role-input" />
        <Input className="h-8 text-sm" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email-input" />
        <Input className="h-8 text-sm" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-phone-input" />
      </div>
      <Button size="sm" variant="outline" onClick={add} disabled={busy} className="rounded-full" data-testid="contact-add-btn">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Plus className="h-3.5 w-3.5 mr-1" />Add contact</>}
      </Button>
    </div>
  );
}

function NotesPanel({ client, onChanged }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/managed/admin/clients/${client.client_id}/notes`, { text }, { headers: authHeaders() });
      setText('');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add note'); }
    finally { setBusy(false); }
  };
  const remove = async (nid) => {
    await axios.delete(`${API}/managed/admin/clients/${client.client_id}/notes/${nid}`, { headers: authHeaders() }).catch(() => toast.error('Delete failed'));
    onChanged();
  };
  return (
    <div className="space-y-2" data-testid="managed-notes-panel">
      <p className="text-sm font-heading font-semibold flex items-center gap-2"><StickyNote className="h-4 w-4 text-amber-400" />Notes</p>
      <div className="flex gap-2">
        <Textarea rows={2} className="text-sm" placeholder="Add a timestamped note..." value={text} onChange={(e) => setText(e.target.value)} data-testid="note-text-input" />
        <Button size="sm" onClick={add} disabled={busy} className="rounded-full self-end bg-amber-500 hover:bg-amber-400 text-slate-950 hover:text-slate-950" data-testid="note-add-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
        </Button>
      </div>
      <div className="space-y-1.5 max-h-64 overflow-y-auto">
        {(client.notes || []).map((n) => (
          <div key={n.note_id} className="rounded-lg bg-muted/60 px-3 py-2 text-sm" data-testid="managed-note-item">
            <p className="leading-snug whitespace-pre-wrap">{n.text}</p>
            <div className="flex items-center justify-between mt-1">
              <p className="text-[11px] text-muted-foreground">{n.author} · {ts(n.created_at)}</p>
              <button onClick={() => remove(n.note_id)} className="text-red-400/70 hover:text-red-400" data-testid={`note-delete-${n.note_id}`}><Trash2 className="h-3 w-3" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function RevenuePanel({ client }) {
  const [rev, setRev] = useState(null);
  useEffect(() => {
    axios.get(`${API}/managed/admin/clients/${client.client_id}/revenue`, { headers: authHeaders() })
      .then((r) => setRev(r.data)).catch(() => {});
  }, [client.client_id, client.organization_id, client.managed_pct]);
  if (!rev) return null;
  return (
    <div className="space-y-2" data-testid="managed-revenue-panel">
      <p className="text-sm font-heading font-semibold flex items-center gap-2"><DollarSign className="h-4 w-4 text-emerald-400" />Revenue ({rev.managed_pct}% managed fee)</p>
      {!rev.organization_id && <p className="text-xs text-muted-foreground">Link an organization to track sales.</p>}
      <div className="grid grid-cols-3 gap-2 text-center">
        {['tickets', 'experiences', 'reservations'].map((k) => (
          <div key={k} className="rounded-lg border border-border p-2">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</p>
            <p className="text-sm font-heading font-bold">{fmt(rev.breakdown[k].gross_cents)}</p>
            <p className="text-[10px] text-muted-foreground">{rev.breakdown[k].count} sales</p>
          </div>
        ))}
      </div>
      <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2 flex items-center justify-between text-sm">
        <span>Your fee ({rev.managed_pct}% of {fmt(rev.total_gross_cents)})</span>
        <span className="font-heading font-bold text-emerald-500" data-testid="managed-fee-total">{fmt(rev.total_fee_cents)}</span>
      </div>
    </div>
  );
}

function OrgLinker({ client, onSave }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (!q.trim()) { setResults([]); return undefined; }
    const t = setTimeout(() => {
      axios.get(`${API}/managed/admin/org-search`, { params: { q }, headers: authHeaders() })
        .then((r) => setResults(r.data.organizations)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-1.5" data-testid="managed-org-linker">
      <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" />Linked organization: <span className="font-mono">{client.organization_id || 'none'}</span>
        {client.organization_id && <button className="text-red-400 underline" onClick={() => onSave({ organization_id: null })} data-testid="org-unlink-btn">unlink</button>}
      </p>
      <Input className="h-8 text-sm" placeholder="Search organizations to link..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="org-search-input" />
      {results.map((o) => (
        <button key={o.organization_id} onClick={() => { onSave({ organization_id: o.organization_id }); setQ(''); setResults([]); }}
          className="block w-full text-left rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-accent" data-testid={`org-result-${o.organization_id}`}>
          {o.display_name} <span className="text-xs text-muted-foreground">· {o.primary_city || o.slug}</span>
        </button>
      ))}
    </div>
  );
}

function ClientRow({ client, onChanged }) {
  const [open, setOpen] = useState(false);
  const [fee, setFee] = useState((client.setup_fee_cents / 100).toString());
  const [pct, setPct] = useState(String(client.managed_pct));

  const save = async (patch) => {
    try {
      await axios.patch(`${API}/managed/admin/clients/${client.client_id}`, patch, { headers: authHeaders() });
      toast.success('Saved');
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const copyPayLink = () => {
    const link = `${window.location.origin}/managed?pay=${client.client_id}`;
    navigator.clipboard.writeText(link).then(() => toast.success('Payment link copied — send it to the customer'));
  };

  const removeClient = async () => {
    if (!window.confirm(`Delete ${client.business_name}? This removes their contacts, notes and record.`)) return;
    try {
      await axios.delete(`${API}/managed/admin/clients/${client.client_id}`, { headers: authHeaders() });
      toast.success('Customer deleted');
      onChanged();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <Card data-testid={`managed-client-${client.client_id}`}>
      <CardContent className="p-4">
        <button className="w-full flex items-center gap-3 text-left" onClick={() => setOpen(!open)} data-testid={`client-toggle-${client.client_id}`}>
          <div className="min-w-0 flex-1">
            <p className="font-heading font-semibold truncate">{client.business_name}</p>
            <p className="text-xs text-muted-foreground truncate">{TYPES[client.business_type] || client.business_type} · {client.email || 'no email'} · joined {ts(client.created_at)}</p>
          </div>
          <Badge variant="outline" className={`text-[10px] ${STATUS_BADGE[client.status] || ''}`}>{client.status.replace('_', ' ')}</Badge>
          <Badge variant="outline" className="text-[10px]">{client.managed_pct}%</Badge>
          {client.setup_paid
            ? <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-500 border-emerald-500/30">setup paid</Badge>
            : <Badge variant="outline" className="text-[10px]">{fmt(client.setup_fee_cents)} due</Badge>}
          {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
        </button>

        {open && (
          <div className="mt-4 pt-4 border-t border-border grid lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex flex-wrap items-end gap-2">
                <div>
                  <p className="text-[11px] text-muted-foreground mb-1">Setup fee ($)</p>
                  <Input className="h-8 w-24 text-sm" type="number" min={0} step="0.01" value={fee} onChange={(e) => setFee(e.target.value)} data-testid="setup-fee-input" />
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground mb-1">Managed %</p>
                  <Input className="h-8 w-20 text-sm" type="number" min={0} max={100} step="0.5" value={pct} onChange={(e) => setPct(e.target.value)} data-testid="managed-pct-input" />
                </div>
                <div>
                  <p className="text-[11px] text-muted-foreground mb-1">Status</p>
                  <select value={client.status} onChange={(e) => save({ status: e.target.value })}
                    className="h-8 px-2 border border-border rounded-md bg-background text-sm" data-testid="client-status-select">
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </select>
                </div>
                <Button size="sm" className="h-8 rounded-full" onClick={() => save({ setup_fee_cents: Math.round(parseFloat(fee || '0') * 100), managed_pct: parseFloat(pct || '0') })} data-testid="client-save-btn">Save</Button>
                {!client.setup_paid && (
                  <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={copyPayLink} data-testid="copy-pay-link-btn">
                    <Copy className="h-3.5 w-3.5 mr-1.5" />Payment link
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-8 rounded-full text-red-400 border-red-500/30" onClick={removeClient} data-testid="client-delete-btn">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              {(client.services || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5" data-testid="client-services-badges">
                  {client.services.map((s) => (
                    <Badge key={s} variant="outline" className="text-[10px] bg-amber-500/10 text-amber-500 border-amber-500/30">{s}</Badge>
                  ))}
                </div>
              )}
              {client.details && <p className="text-sm text-muted-foreground bg-muted/50 rounded-lg p-3">"{client.details}"</p>}
              <OrgLinker client={client} onSave={save} />
              <RevenuePanel client={client} />
            </div>
            <div className="space-y-5">
              <ContactsPanel client={client} onChanged={onChanged} />
              <NotesPanel client={client} onChanged={onChanged} />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminManagedServices() {
  const [clients, setClients] = useState(null);
  const [filter, setFilter] = useState('');
  const [addForm, setAddForm] = useState({ business_name: '', email: '', phone: '', business_type: 'event_creator' });
  const [showAdd, setShowAdd] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/managed/admin/clients`, { headers: authHeaders() })
      .then((r) => setClients(r.data.clients))
      .catch(() => toast.error('Could not load — admin login required'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addClient = async () => {
    if (!addForm.business_name.trim()) { toast.error('Business name required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/managed/admin/clients`, addForm, { headers: authHeaders() });
      toast.success('Customer added');
      setAddForm({ business_name: '', email: '', phone: '', business_type: 'event_creator' });
      setShowAdd(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not add'); }
    finally { setBusy(false); }
  };

  if (!clients) return <div className="min-h-screen bg-background pt-24 text-center text-muted-foreground">Loading…</div>;

  const shown = filter ? clients.filter((c) => c.status === filter) : clients;

  return (
    <div className="min-h-screen bg-background pt-20 pb-16" data-testid="admin-managed-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 space-y-6">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold">Managed Services — Customers</h1>
            <p className="text-sm text-muted-foreground">Setup fees, managed percentages, contacts, notes and revenue per customer.</p>
          </div>
          <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 hover:text-slate-950" data-testid="add-client-toggle-btn">
            <Plus className="h-4 w-4 mr-1.5" />Add customer
          </Button>
        </div>

        {showAdd && (
          <Card data-testid="add-client-form">
            <CardContent className="p-4 flex flex-wrap items-center gap-2">
              <Input className="h-9 flex-1 min-w-[180px]" placeholder="Business name *" value={addForm.business_name} onChange={(e) => setAddForm({ ...addForm, business_name: e.target.value })} data-testid="add-business-input" />
              <Input className="h-9 flex-1 min-w-[160px]" placeholder="Email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} data-testid="add-email-input" />
              <Input className="h-9 w-36" placeholder="Phone" value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} data-testid="add-phone-input" />
              <select className="h-9 px-2 border border-border rounded-md bg-background text-sm" value={addForm.business_type} onChange={(e) => setAddForm({ ...addForm, business_type: e.target.value })} data-testid="add-type-select">
                <option value="event_creator">Event creator</option>
                <option value="restaurant">Restaurant</option>
                <option value="venue">Venue</option>
                <option value="other">Other</option>
              </select>
              <Button size="sm" onClick={addClient} disabled={busy} className="rounded-full" data-testid="add-client-submit-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
              </Button>
            </CardContent>
          </Card>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          {['', ...STATUSES].map((s) => (
            <button key={s || 'all'} onClick={() => setFilter(s)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${filter === s ? 'border-amber-500 bg-amber-500/10 text-amber-500' : 'border-border hover:bg-accent'}`}
              data-testid={`filter-${s || 'all'}`}>
              {s ? s.replace('_', ' ') : 'All'} ({s ? clients.filter((c) => c.status === s).length : clients.length})
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {shown.length === 0 && <p className="text-sm text-muted-foreground py-8 text-center">No customers here yet — they'll appear as they sign up at /managed.</p>}
          {shown.map((c) => <ClientRow key={c.client_id} client={c} onChanged={load} />)}
        </div>
      </div>
    </div>
  );
}
